import { inject, Injectable } from '@angular/core';
import {
  IConstraintViolation,
  IContact,
  IOrganization,
  ITag,
  IValidatedContactBatchLine,
} from '../../types/contactModule';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  concatMap,
  debounceTime,
  from,
  last,
  map,
  mergeMap,
  tap,
  throwError,
} from 'rxjs';
import { ApiService } from '../api/api.service';
import { UserProfileService } from '../user/user-profile.service';
import { getCountryCode, parseFlexiblePhoneNumber } from '../../../utils/utils';
import { toCsv } from '../../../utils/csv';
import { SecurePersistStateService } from '../storage/local-storage.service';
import type { CountryCode } from 'libphonenumber-js';

export interface IBatchUploadProgress {
  isBatching: boolean;
  currentBatch: number;
  totalBatches: number;
  percentage: number;
}

interface IUploadResult {
  successfulBatchLines: IValidatedContactBatchLine[];
  failedBatchLines: IValidatedContactBatchLine[];
}

/** What the last upload did: rows sent, rows created, rows rejected. */
export interface IUploadSummary {
  sent: number;
  created: number;
  failed: number;
}

/** Marks the violation added for a number that appears twice in one file. */
const DUPLICATE_MARK = 'DUPLICATE_IN_FILE';
const SUMMARY_KEY = 'csvUploadSummary';

@Injectable({
  providedIn: 'root',
})
export class CsvServiceService {
  constructor() {}
  apiService = inject(ApiService);
  userService = inject(UserProfileService);
  storageService = inject(SecurePersistStateService);

  serviceName = 'blackkonnectcxpcontacts';
  get organizationId() {
    return this.userService.organizationId;
  }

  private tableHeaders: string[] = [];
  private tableData: string[][] = [];
  /** Spreadsheet row number (1-based) of each entry in tableData; blank rows are not in tableData. */
  private tableRowNumbers: number[] = [];
  private objectifiedData: (IContact & Record<string, any>)[] = [];

  // private validatedContacts = new BehaviorSubject<IValidatedContactBatchLine[]>(
  //   []
  // );
  private validatedContacts: IValidatedContactBatchLine[] = [];
  private createdContacts: IValidatedContactBatchLine[] = [];
  // successful contacts uploaded
  private successfulUploadedContacts: IValidatedContactBatchLine[] = [];
  // failed contacts uploaded
  private failedUploadedContacts: IValidatedContactBatchLine[] = [];
  private uploadSummary: IUploadSummary | null = null;
  isLoading: boolean = false;

  /**
   * Goes up on every change to the import rows, so tables can cache their
   * filtered view and still notice in-place edits (status flips, deletes).
   */
  private _version = 0;
  get version(): number {
    return this._version;
  }
  private bump() {
    this._version++;
  }

  // Batch upload progress (only emits when we fall back to batched upload after a 413)
  private batchUploadProgressSubject = new BehaviorSubject<IBatchUploadProgress>({
    isBatching: false,
    currentBatch: 0,
    totalBatches: 0,
    percentage: 0,
  });
  batchUploadProgress$ = this.batchUploadProgressSubject.asObservable();

  // Default batch size used after a 413. Halves recursively per sub-batch if a single
  // batch still exceeds the server limit.
  private readonly DEFAULT_BATCH_SIZE = 500;

  // How many validation batches can be in-flight concurrently. Validation has
  // no side effects so parallelism is safe and slashes wall-clock time on large
  // imports (sequential 10×1.5s = 15s vs parallel-3 ≈ 5s).
  private readonly VALIDATION_BATCH_CONCURRENCY = 3;

  // Above this serialized payload size we skip the doomed full request and batch
  // immediately. Most gateways in front of the API cap bodies in the 4-10 MB range
  // and respond without CORS, so the browser only sees an opaque "Failed to fetch".
  // 2 MB gives generous headroom while still avoiding that failure mode.
  private readonly PAYLOAD_BYTE_THRESHOLD = 2 * 1024 * 1024;

  // Storage layer:
  // Working data (validated/successful/failed contacts) can be megabytes for
  // large imports. SecurePersistStateService re-encrypts the entire app state
  // synchronously per write, which froze the UI during edits. For this transient
  // working data we use plain localStorage and coalesce rapid writes via a Subject
  // so a burst of edits collapses to a single serialize+write.
  private readonly STORAGE_DEBOUNCE_MS = 250;
  private storageDirty$ = new Subject<{
    key: 'validatedContacts' | 'successfulUploadedContacts' | 'failedUploadedContacts';
  }>();

  private initStorageWriter() {
    if ((this as any)._storageWriterInit) return;
    (this as any)._storageWriterInit = true;
    this.storageDirty$
      .pipe(debounceTime(this.STORAGE_DEBOUNCE_MS))
      .subscribe(({ key }) => this.flushStorage(key));
  }

  private flushStorage(
    key: 'validatedContacts' | 'successfulUploadedContacts' | 'failedUploadedContacts'
  ): void {
    const value =
      key === 'validatedContacts'
        ? this.validatedContacts
        : key === 'successfulUploadedContacts'
        ? this.successfulUploadedContacts
        : this.failedUploadedContacts;
    try {
      this.storageService.setItem(key, value);
    } catch (err) {
    }
  }

  private markStorageDirty(
    key: 'validatedContacts' | 'successfulUploadedContacts' | 'failedUploadedContacts'
  ): void {
    this.initStorageWriter();
    this.storageDirty$.next({ key });
  }

  private readFromStorage<T>(key: string): T | null {
    try {
      return this.storageService.getItem(key) as T | null;
    } catch {
      return null;
    }
  }

  updateTableHeaders(headers: string[]): void {
    this.tableHeaders = headers;
  }
  updateTableData(data: string[][]): void {
    this.tableData = data;
  }
  updateTableRowNumbers(rows: number[]): void {
    this.tableRowNumbers = rows;
  }

  get getTableHeaders(): string[] {
    return this.tableHeaders;
  }
  get getTableData(): string[][] {
    return this.tableData;
  }

  // generate the objectified data
  generateObjectifiedData(headers: string[], data: string[][]) {
    let result: any[] = [];
    if (this.tableHeaders.length > 0 && this.tableData.length > 0) {
      this.tableData.forEach((row) => {
        let obj: any = {};
        this.tableHeaders.forEach((header, index) => {
          obj[header] = row[index];
        });
        result.push(obj);
      });
    }
    this.objectifiedData = result;
  }

  // update the objectified data after column mapping
  updateObjectifiedData(data: (IContact & Record<string, any>)[]) {
    this.objectifiedData = data;
  }

  // ---- CLIENT-SIDE VALIDATION (Clean Up step) -------------------------------
  // Runs cheap, local rules over the mapped contacts before any backend call.
  // Populates validatedContacts in the same IValidatedContactBatchLine shape the
  // downstream review/feedback flow already understands.

  // Validation runs libphonenumber parsing + country lookups per row, which for
  // 1000+ contacts blocks the main thread long enough to freeze the UI. We process
  // the rows in chunks and yield to the event loop between chunks so the page stays
  // responsive (and any loading indicator keeps animating). Returns a Promise that
  // resolves once every row has been validated.
  private readonly VALIDATION_CHUNK_SIZE = 200;

  applyMappedContactsAsValidated(
    contacts: (IContact & Record<string, any>)[]
  ): Promise<void> {
    const buildLine = (
      contact: IContact & Record<string, any>,
      idx: number
    ): IValidatedContactBatchLine => {
      const violations = this.runLocalValidation(contact);
      return {
        personEntity: { ...contact, id: `${idx}` },
        batchLineValidationStatus:
          violations.length === 0 ? 'PROCESSED_OK' : 'PROCESSING_ERROR',
        constraintViolations:
          violations.length === 0
            ? [
                {
                  message: 'Successful',
                  propertyName: '',
                  invalidValue: '',
                  constraintDescription: '',
                },
              ]
            : violations,
      };
    };

    const finalize = (lines: IValidatedContactBatchLine[]) => {
      this.validatedContacts = lines;
      this.flagDuplicateNumbers(lines);
      this.bump();
      // We've copied every contact's field set into the new batch-line wrappers,
      // so the raw mapped data can be released. Keep tableHeaders/tableData since
      // the user may navigate back to the mapping step to redo column mapping.
      this.objectifiedData = [];
      this.markStorageDirty('validatedContacts');
    };

    // Small datasets: stay synchronous to avoid an unnecessary tick.
    if (contacts.length <= this.VALIDATION_CHUNK_SIZE) {
      finalize(contacts.map(buildLine));
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      const lines: IValidatedContactBatchLine[] = new Array(contacts.length);
      let i = 0;
      const processChunk = () => {
        const end = Math.min(i + this.VALIDATION_CHUNK_SIZE, contacts.length);
        for (; i < end; i++) {
          lines[i] = buildLine(contacts[i], i);
        }
        if (i < contacts.length) {
          setTimeout(processChunk, 0);
        } else {
          finalize(lines);
          resolve();
        }
      };
      processChunk();
    });
  }

  // Returns the list of constraint violations for a single contact. Empty when valid.
  runLocalValidation(contact: IContact): IConstraintViolation[] {
    const violations: IConstraintViolation[] = [];

    // Phone: required + parseable via libphonenumber-js. Try international form first,
    // then fall back to combining the mapped country code with the local number.
    const phone = (contact.primaryContactNumber ?? '').toString().trim();
    if (!phone) {
      violations.push(this.violation(
        'primaryContactNumber',
        phone,
        'Phone number is required'
      ));
    } else if (!this.isPhoneNumberValid(phone, contact)) {
      const hasRegion = !!(contact as any).countryCode;
      violations.push(this.violation(
        'primaryContactNumber',
        phone,
        !phone.startsWith('+') && !hasRegion
          ? 'Number has no country code. Add one, or set a country for this row'
          : 'Phone number is not valid'
      ));
    }

    // Email: optional, but must be a valid format when present.
    const email = (contact.emailAddress ?? '').toString().trim();
    if (email && !this.isEmailValid(email)) {
      violations.push(this.violation(
        'emailAddress',
        email,
        'Email address is not valid'
      ));
    }

    // Birth date: optional. Column-mapping parses provided values to ISO, leaves it
    // as '' when no value was mapped/entered, and returns null only when a non-empty
    // value could not be parsed. Only that last case is a violation.
    const rawBirthDate = (contact as any).birthDate;
    if (rawBirthDate === null) {
      violations.push(this.violation(
        'birthDate',
        '',
        'Birth date is not a valid date'
      ));
    }

    // Country: optional, but if a value was provided it must resolve to a known country code.
    const country = (contact.country ?? '').toString().trim();
    if (country && !getCountryCode(country)?.countryCode) {
      violations.push(this.violation(
        'country',
        country,
        'Country is not recognised'
      ));
    }

    return violations;
  }

  private violation(
    propertyName: string,
    invalidValue: string,
    message: string
  ): IConstraintViolation {
    return { message, propertyName, invalidValue, constraintDescription: message };
  }

  private isEmailValid(email: string): boolean {
    // Pragmatic RFC 5322-ish check — same spirit as Angular's Validators.email.
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private isPhoneNumberValid(
    phone: string,
    contact: IContact & Record<string, any>
  ): boolean {
    // `countryCode` is the alpha-2 region derived during column mapping
    // (e.g. "GH"), used as the default region for national-format numbers.
    // The flexible parser also rescues numbers whose leading "+" was stripped
    // by Excel (e.g. "233241234567") instead of prepending the code twice.
    const region = (contact as any).countryCode as CountryCode | undefined;
    return !!parseFlexiblePhoneNumber(phone, region);
  }

  updateValidatedContact(contact: IContact, index: string | number) {
    this.validatedContacts = this.getValidatedContacts;
    const target = String(contact.id);
    let contactIndex = -1;
    for (let i = 0; i < this.validatedContacts.length; i++) {
      if (String(this.validatedContacts[i].personEntity.id) === target) {
        contactIndex = i;
        break;
      }
    }
    if (contactIndex < 0) return;
    const contactToUpdate = this.validatedContacts[contactIndex];
    // Re-run client-side validation against the edited values so the row's
    // status and violations reflect the new state (Clean Up step relies on this).
    const violations = this.runLocalValidation(contact);
    this.validatedContacts[contactIndex] = {
      ...contactToUpdate,
      batchLineValidationStatus:
        violations.length === 0 ? 'PROCESSED_OK' : 'PROCESSING_ERROR',
      constraintViolations:
        violations.length === 0
          ? [
              {
                message: 'Successful',
                propertyName: '',
                invalidValue: '',
                constraintDescription: '',
              },
            ]
          : violations,
      personEntity: contact,
    };
    this.flagDuplicateNumbers(this.validatedContacts);
    this.bump();
    this.updateValidatedContactsInStorage();
  }

  deleteValidatedContact(index: string | number) {
    this.validatedContacts = this.getValidatedContacts;
    const contactIndex = this.validatedContacts.findIndex(
      (el) => el.personEntity.id == index
    );
    // Not found: remove nothing (splice(-1) would drop the last row).
    if (contactIndex < 0) return;
    this.validatedContacts.splice(contactIndex, 1);
    this.flagDuplicateNumbers(this.validatedContacts);
    this.bump();
    this.updateValidatedContactsInStorage();
  }

  deleteBatchOfContacts(indexes: (string | number)[]) {
    this.validatedContacts = this.getValidatedContacts;
    const toDelete = new Set(indexes.map((i) => String(i)));
    this.validatedContacts = this.validatedContacts.filter(
      (contact) => !toDelete.has(String(contact.personEntity.id))
    );
    this.flagDuplicateNumbers(this.validatedContacts);
    this.bump();
    this.updateValidatedContactsInStorage();
  }

  // Build an id→batchLine map once so bulk assigns are O(N+M) instead of O(N×M).
  private buildContactsById(): Map<string, IValidatedContactBatchLine> {
    const map = new Map<string, IValidatedContactBatchLine>();
    for (const line of this.validatedContacts) {
      const id = line.personEntity.id;
      if (id !== undefined && id !== null) map.set(String(id), line);
    }
    return map;
  }

  assignSelectedOrganizationsToSelectedContacts(
    selectedContactsIndexes: (string | number)[],
    selectedOrganizations: IOrganization[]
  ) {
    this.validatedContacts = this.getValidatedContacts;
    const byId = this.buildContactsById();
    for (const index of selectedContactsIndexes) {
      const contactToUpdate = byId.get(String(index));
      if (!contactToUpdate) continue;
      const merged = [
        ...(contactToUpdate.personEntity.companyEntities ?? []),
        ...selectedOrganizations,
      ];
      contactToUpdate.personEntity.companyEntities = this.removeDuplicates(
        merged,
        (org) => org.id
      );
    }
    this.bump();
    this.updateValidatedContactsInStorage();
  }

  assignSelectedTagsToSelectedContacts(
    selectedContactsIndexes: (string | number)[],
    selectedTags: ITag[]
  ) {
    this.validatedContacts = this.getValidatedContacts;
    const byId = this.buildContactsById();
    for (const index of selectedContactsIndexes) {
      const contactToUpdate = byId.get(String(index));
      if (!contactToUpdate) continue;
      const merged = [
        ...(contactToUpdate.personEntity.entityTags ?? []),
        ...selectedTags,
      ];
      contactToUpdate.personEntity.entityTags = this.removeDuplicates(
        merged,
        (tag) => tag.id
      );
    }
    this.bump();
    this.updateValidatedContactsInStorage();
  }

  /**
   * Utility function to remove duplicates from an array based on a unique key.
   */
  removeDuplicates<T>(array: T[], keyExtractor: (item: T) => any): T[] {
    const seen = new Set();
    return array.filter((item) => {
      const key = keyExtractor(item);
      if (seen.has(key)) {
        return false; // Skip if the key is already seen
      }
      seen.add(key);
      return true; // Include if the key is new
    });
  }

  validateContacts(
    contactList: (IContact & Record<string, any>)[]
  ): Observable<{ contactsBatchLines: IValidatedContactBatchLine[] }> {
    this.isLoading = true;
    this.resetBatchProgress();

    const payloadSize = this.payloadBytes({ contacts: contactList });

    // Skip the full request when it's obviously too large for the gateway —
    // a "Failed to fetch" / status:0 doesn't carry the 413 we'd otherwise catch.
    if (payloadSize > this.PAYLOAD_BYTE_THRESHOLD) {
      return this.validateInBatches(contactList);
    }

    return this.postValidationBatch(contactList).pipe(
      tap((res) => {
        this.storeValidationResults(res.contactsBatchLines);
        this.isLoading = false;
      }),
      catchError((err: any) => {
        if (
          this.isPayloadTooLarge(err) ||
          (this.looksLikeOversizedConnectionDrop(err) && contactList.length > 1)
        ) {
          return this.validateInBatches(contactList);
        }
        this.isLoading = false;
        return throwError(() => err);
      })
    );
  }

  // Detects "payload too large" across the common ways it surfaces: direct
  // HttpErrorResponse, nested error body, or string-based status from a gateway.
  private isPayloadTooLarge(err: any): boolean {
    if (!err) return false;
    if (err.status === 413 || err.status === '413') return true;
    if (err?.error?.status === 413) return true;
    const msg = (
      err?.message ||
      err?.error?.message ||
      err?.statusText ||
      ''
    ).toString().toLowerCase();
    return msg.includes('payload too large') || msg.includes('request entity too large');
  }

  // Gateways that reject oversized bodies often respond without CORS headers, so
  // the browser surfaces an opaque "Failed to fetch" / status: 0. Treat that as a
  // size failure when we still have more than one row to split.
  private looksLikeOversizedConnectionDrop(err: any): boolean {
    if (!err) return false;
    if (err.status !== 0) return false;
    const msg = (err?.message || err?.error?.message || '').toString();
    return msg.toLowerCase().includes('failed to fetch') || err?.name === 'HttpErrorResponse';
  }

  // Estimates serialized JSON size without fully stringifying a megabyte payload.
  // For payloads that look like { contacts: [...] } or { contactsBatchLines: [...] }
  // we sample a handful of items and extrapolate; otherwise we fall back to the
  // exact stringify path. The estimate only needs to be good enough to decide
  // whether we're over the gateway's ~2MB threshold.
  private payloadBytes(payload: unknown): number {
    try {
      const arr = this.extractItemArray(payload);
      if (!arr || arr.length === 0) {
        return new Blob([JSON.stringify(payload)]).size;
      }
      const sampleSize = Math.min(10, arr.length);
      let sampleBytes = 0;
      for (let i = 0; i < sampleSize; i++) {
        sampleBytes += JSON.stringify(arr[i]).length;
      }
      const avgItemBytes = sampleBytes / sampleSize;
      // ~+1 byte per item for commas, ~50 bytes for the envelope keys/braces.
      return Math.round(avgItemBytes * arr.length + arr.length + 50);
    } catch {
      try {
        return new Blob([JSON.stringify(payload)]).size;
      } catch {
        return Number.MAX_SAFE_INTEGER;
      }
    }
  }

  private extractItemArray(payload: any): any[] | null {
    if (!payload || typeof payload !== 'object') return null;
    if (Array.isArray(payload.contacts)) return payload.contacts;
    if (Array.isArray(payload.contactsBatchLines)) return payload.contactsBatchLines;
    return null;
  }

  private postValidationBatch(
    contacts: (IContact & Record<string, any>)[]
  ): Observable<{ contactsBatchLines: IValidatedContactBatchLine[] }> {
    return this.apiService.post<{
      contactsBatchLines: IValidatedContactBatchLine[];
    }>(
      `${this.serviceName}/api/person-entities/organisations/${this.organizationId}/contacts/batch/import/validation`,
      { contacts }
    );
  }

  private validateInBatches(
    allContacts: (IContact & Record<string, any>)[]
  ): Observable<{ contactsBatchLines: IValidatedContactBatchLine[] }> {
    const batchSize = Math.min(this.DEFAULT_BATCH_SIZE, allContacts.length);
    const batches = this.chunk(allContacts, batchSize);
    const totalBatches = batches.length;

    this.batchUploadProgressSubject.next({
      isBatching: true,
      currentBatch: 0,
      totalBatches,
      percentage: 0,
    });

    const aggregated: IValidatedContactBatchLine[] = [];
    let completed = 0;

    return from(batches).pipe(
      mergeMap(
        (batch) =>
          this.validateBatchWithSubdivision(batch).pipe(
            tap((res) => {
              aggregated.push(...(res.contactsBatchLines || []));
              completed += 1;
              this.batchUploadProgressSubject.next({
                isBatching: completed < totalBatches,
                currentBatch: completed,
                totalBatches,
                percentage: Math.round((completed / totalBatches) * 100),
              });
            })
          ),
        this.VALIDATION_BATCH_CONCURRENCY
      ),
      last(),
      map(() => {
        this.storeValidationResults(aggregated);
        this.isLoading = false;
        return { contactsBatchLines: aggregated };
      }),
      catchError((err) => {
        this.resetBatchProgress();
        this.isLoading = false;
        return throwError(() => err);
      })
    );
  }

  private validateBatchWithSubdivision(
    batch: (IContact & Record<string, any>)[]
  ): Observable<{ contactsBatchLines: IValidatedContactBatchLine[] }> {
    return this.postValidationBatch(batch).pipe(
      catchError((err: any) => {
        const isOversize =
          this.isPayloadTooLarge(err) ||
          this.looksLikeOversizedConnectionDrop(err);
        if (isOversize && batch.length > 1) {
          const half = Math.max(1, Math.floor(batch.length / 2));
          const subBatches = this.chunk(batch, half);
          const collected: IValidatedContactBatchLine[] = [];
          return from(subBatches).pipe(
            concatMap((sb) =>
              this.validateBatchWithSubdivision(sb).pipe(
                tap((res) => collected.push(...(res.contactsBatchLines || [])))
              )
            ),
            last(),
            map(() => ({ contactsBatchLines: collected }))
          );
        }
        return throwError(() => err);
      })
    );
  }

  private storeValidationResults(lines: IValidatedContactBatchLine[]) {
    // Each row was sent with its place in the file as its id, and the server
    // echoes it back. The answer itself comes in no particular order (a set on
    // the server, and batches finish in any order), so keep those ids and put
    // the rows back in file order; row numbers then still point at the right
    // rows. Only when an id is missing or repeated are ids given by position.
    const seen = new Set<string>();
    const echoed = lines.every((line) => {
      if (!line.personEntity) return true;
      const id = line.personEntity.id;
      const key = id === null || id === undefined ? '' : String(id);
      if (!/^\d+$/.test(key) || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (echoed) {
      for (const line of lines) {
        if (line.personEntity) line.personEntity.id = String(line.personEntity.id);
      }
      lines.sort((a, b) => Number(a.personEntity?.id ?? 0) - Number(b.personEntity?.id ?? 0));
    } else {
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.personEntity) line.personEntity.id = `${i}`;
      }
    }
    this.validatedContacts = lines;
    this.bump();
    this.markStorageDirty('validatedContacts');
  }

  uploadAndCreateContacts(): Observable<IUploadResult> {
    this.isLoading = true;
    this.resetBatchProgress();
    const allContacts = this.getValidatedContacts;
    this.lastSentCount = allContacts.length;

    const payload = {
      batchValidationStatus: 'PROCESSED_OK',
      contactsBatchLines: allContacts,
    };
    const payloadSize = this.payloadBytes(payload);

    if (payloadSize > this.PAYLOAD_BYTE_THRESHOLD) {
      return this.uploadInBatches(allContacts);
    }

    return this.postContactsBatch(allContacts).pipe(
      tap((res) => {
        this.storeUploadResults(res);
        this.isLoading = false;
      }),
      catchError((err: any) => {
        if (
          this.isPayloadTooLarge(err) ||
          (this.looksLikeOversizedConnectionDrop(err) && allContacts.length > 1)
        ) {
          return this.uploadInBatches(allContacts);
        }
        this.isLoading = false;
        return throwError(() => err);
      })
    );
  }

  private postContactsBatch(
    contacts: IValidatedContactBatchLine[]
  ): Observable<IUploadResult> {
    return this.apiService.post<IUploadResult>(
      `${this.serviceName}/api/person-entities/organisations/${this.organizationId}/contacts/batch/import`,
      {
        batchValidationStatus: 'PROCESSED_OK',
        contactsBatchLines: contacts,
      }
    );
  }

  private uploadInBatches(
    allContacts: IValidatedContactBatchLine[]
  ): Observable<IUploadResult> {
    const batchSize = Math.min(this.DEFAULT_BATCH_SIZE, allContacts.length);
    const batches = this.chunk(allContacts, batchSize);
    const totalBatches = batches.length;

    this.batchUploadProgressSubject.next({
      isBatching: true,
      currentBatch: 0,
      totalBatches,
      percentage: 0,
    });

    const aggregated: IUploadResult = {
      successfulBatchLines: [],
      failedBatchLines: [],
    };
    // Rows the server has already answered for. They are saved (or rejected)
    // even if a later batch fails, so they must not be sent again.
    const sentLines: IValidatedContactBatchLine[] = [];
    const onSent = (lines: IValidatedContactBatchLine[], res: IUploadResult) => {
      sentLines.push(...lines);
      aggregated.successfulBatchLines.push(...(res.successfulBatchLines || []));
      aggregated.failedBatchLines.push(...(res.failedBatchLines || []));
    };

    return from(batches).pipe(
      concatMap((batch, idx) =>
        this.uploadBatchWithSubdivision(batch, onSent).pipe(
          tap(() => {
            const completed = idx + 1;
            this.batchUploadProgressSubject.next({
              isBatching: completed < totalBatches,
              currentBatch: completed,
              totalBatches,
              percentage: Math.round((completed / totalBatches) * 100),
            });
          })
        )
      ),
      last(),
      map(() => {
        this.storeUploadResults(aggregated);
        this.isLoading = false;
        return aggregated;
      }),
      catchError((err) => {
        this.resetBatchProgress();
        this.isLoading = false;
        if (sentLines.length) this.carryOverPartialUpload(aggregated, sentLines);
        return throwError(() => err);
      })
    );
  }

  // Recursively halves a batch when the server still returns 413 for it,
  // so we adapt to whatever the server's true payload ceiling is. `onSent`
  // runs for every request the server accepted, with the rows it carried.
  private uploadBatchWithSubdivision(
    batch: IValidatedContactBatchLine[],
    onSent: (lines: IValidatedContactBatchLine[], res: IUploadResult) => void
  ): Observable<void> {
    return this.postContactsBatch(batch).pipe(
      tap((res) => onSent(batch, res)),
      map(() => undefined),
      catchError((err: any) => {
        const isOversize =
          this.isPayloadTooLarge(err) ||
          this.looksLikeOversizedConnectionDrop(err);
        if (isOversize && batch.length > 1) {
          const half = Math.max(1, Math.floor(batch.length / 2));
          const subBatches = this.chunk(batch, half);
          return from(subBatches).pipe(
            concatMap((sb) => this.uploadBatchWithSubdivision(sb, onSent)),
            last(),
            map(() => undefined)
          );
        }
        return throwError(() => err);
      })
    );
  }

  /** Rows sent by the upload in progress, for the result summary. */
  private lastSentCount = 0;

  /**
   * Results of batches that went through before a batched upload stopped.
   * Those rows leave the import list; the next upload adds its results to
   * these so the Result step covers both tries.
   */
  private carriedResults: IUploadResult | null = null;
  private carriedSent = 0;

  /** Rows already sent and contacts already added by an upload that stopped part way. */
  get partialUpload(): { sent: number; added: number } | null {
    if (!this.carriedResults) return null;
    return { sent: this.carriedSent, added: this.carriedResults.successfulBatchLines.length };
  }

  private carryOverPartialUpload(done: IUploadResult, sentLines: IValidatedContactBatchLine[]) {
    const previous = this.carriedResults;
    this.carriedResults = {
      successfulBatchLines: [...(previous?.successfulBatchLines ?? []), ...done.successfulBatchLines],
      failedBatchLines: [...(previous?.failedBatchLines ?? []), ...done.failedBatchLines],
    };
    this.carriedSent += sentLines.length;
    const sent = new Set(sentLines);
    this.validatedContacts = this.getValidatedContacts.filter((line) => !sent.has(line));
    this.bump();
    this.updateValidatedContactsInStorage();
  }

  private storeUploadResults(res: IUploadResult) {
    const carried = this.carriedResults;
    const carriedSent = this.carriedSent;
    this.carriedResults = null;
    this.carriedSent = 0;
    this.createdContacts = res as any;
    this.successfulUploadedContacts = [
      ...(carried?.successfulBatchLines ?? []),
      ...(res.successfulBatchLines || []),
    ];
    this.failedUploadedContacts = [...(carried?.failedBatchLines ?? []), ...(res.failedBatchLines || [])];
    this.uploadSummary = {
      sent: this.lastSentCount + carriedSent,
      created: this.successfulUploadedContacts.length,
      failed: this.failedUploadedContacts.length,
    };
    try {
      this.storageService.setItem(SUMMARY_KEY, this.uploadSummary);
    } catch {}
    this._hydratedSummary = true;
    this.bump();
    // Free the pre-upload buffer in memory and storage: every contact is now in
    // either the success or fail bucket. Avoids carrying megabytes of duplicate
    // state through the Feedback view.
    this.validatedContacts = [];
    try {
      localStorage.removeItem('validatedContacts');
    } catch {}
    this.markStorageDirty('successfulUploadedContacts');
    this.markStorageDirty('failedUploadedContacts');
  }

  private resetBatchProgress() {
    this.batchUploadProgressSubject.next({
      isBatching: false,
      currentBatch: 0,
      totalBatches: 0,
      percentage: 0,
    });
  }

  private chunk<T>(arr: T[], size: number): T[][] {
    if (size <= 0) return [arr];
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      out.push(arr.slice(i, i + size));
    }
    return out;
  }

  clearCsvData() {
    this.tableData = [];
    this.tableHeaders = [];
    this.tableRowNumbers = [];
    this.objectifiedData = [];
    this.validatedContacts = [];
    this.createdContacts = [];
    this.successfulUploadedContacts = [];
    this.failedUploadedContacts = [];
    this.uploadSummary = null;
    this.carriedResults = null;
    this.carriedSent = 0;
    this.phoneKeyCache.clear();
    this._hydratedValidated = true;
    this._hydratedSuccessful = true;
    this._hydratedFailed = true;
    this._hydratedSummary = true;
    try {
      localStorage.removeItem('validatedContacts');
      localStorage.removeItem('successfulUploadedContacts');
      localStorage.removeItem('failedUploadedContacts');
      localStorage.removeItem(SUMMARY_KEY);
    } catch {}
    this.bump();
    this.resetBatchProgress();
  }

  updateValidatedContactsInStorage() {
    this.markStorageDirty('validatedContacts');
  }

  updateSuccessfulContacts(contacts: IValidatedContactBatchLine[]) {
    this.successfulUploadedContacts = contacts;
    this.bump();
    this.markStorageDirty('successfulUploadedContacts');
  }

  updateFailedContacts(contacts: IValidatedContactBatchLine[]) {
    this.failedUploadedContacts = contacts;
    this.bump();
    this.markStorageDirty('failedUploadedContacts');
  }

  get getObjectifiedData() {
    return this.objectifiedData;
  }

  // Track whether we've already attempted to hydrate from storage so subsequent
  // accesses with an empty in-memory array don't re-read localStorage every CD cycle.
  private _hydratedValidated = false;
  private _hydratedSuccessful = false;
  private _hydratedFailed = false;
  private _hydratedSummary = false;

  get getValidatedContacts(): IValidatedContactBatchLine[] {
    if (this.validatedContacts.length) return this.validatedContacts;
    if (this._hydratedValidated) return this.validatedContacts;
    this._hydratedValidated = true;
    const stored = this.readFromStorage<IValidatedContactBatchLine[]>('validatedContacts');
    this.validatedContacts = stored ?? [];
    return this.validatedContacts;
  }

  get getCreatedContacts() {
    return this.createdContacts;
  }

  get getSuccessfulContacts() {
    if (this.successfulUploadedContacts.length) return this.successfulUploadedContacts;
    if (this._hydratedSuccessful) return this.successfulUploadedContacts;
    this._hydratedSuccessful = true;
    const stored = this.readFromStorage<IValidatedContactBatchLine[]>('successfulUploadedContacts');
    this.successfulUploadedContacts = stored ?? [];
    return this.successfulUploadedContacts;
  }

  get getFailedContacts() {
    if (this.failedUploadedContacts.length) return this.failedUploadedContacts;
    if (this._hydratedFailed) return this.failedUploadedContacts;
    this._hydratedFailed = true;
    const stored = this.readFromStorage<IValidatedContactBatchLine[]>('failedUploadedContacts');
    this.failedUploadedContacts = stored ?? [];
    return this.failedUploadedContacts;
  }

  /** Rows sent, created and rejected by the last upload, or null before one. */
  get getUploadSummary(): IUploadSummary | null {
    if (this.uploadSummary || this._hydratedSummary) return this.uploadSummary;
    this._hydratedSummary = true;
    this.uploadSummary = this.readFromStorage<IUploadSummary>(SUMMARY_KEY);
    return this.uploadSummary;
  }

  /**
   * The row's number in the spreadsheet, as shown in the import tables. A
   * row's id is its place among the file's data rows (kept through edits and
   * the server check); the header row and any blank rows are counted back in.
   */
  rowNumber(line: IValidatedContactBatchLine): number | null {
    const id = Number(line?.personEntity?.id);
    if (!Number.isInteger(id) || id < 0) return null;
    // After a reload the parsed file is gone: assume one header row, no gaps.
    return this.tableRowNumbers[id] ?? id + 2;
  }

  /**
   * Flags every row whose phone number already appeared earlier in the file.
   * The backend does not catch duplicates inside one upload, so without this
   * both rows would be created and the person messaged twice.
   */
  flagDuplicateNumbers(lines: IValidatedContactBatchLine[]): number {
    const firstSeen = new Map<string, number | null>();
    let duplicates = 0;
    for (const line of lines) {
      if (!line?.personEntity) continue;
      // Drop any earlier duplicate flag; it is worked out afresh.
      const kept = (line.constraintViolations ?? []).filter(
        (v) => v.constraintDescription !== DUPLICATE_MARK && v.message !== 'Successful'
      );
      const key = this.phoneKey(line.personEntity);
      if (key && firstSeen.has(key)) {
        const first = firstSeen.get(key);
        kept.push({
          propertyName: 'primaryContactNumber',
          invalidValue: `${line.personEntity.primaryContactNumber ?? ''}`,
          message: first ? `Same number as row ${first}` : 'Same number as another row',
          constraintDescription: DUPLICATE_MARK,
        });
        duplicates++;
      } else if (key) {
        firstSeen.set(key, this.rowNumber(line));
      }
      line.batchLineValidationStatus = kept.length ? 'PROCESSING_ERROR' : 'PROCESSED_OK';
      line.constraintViolations = kept.length
        ? kept
        : [{ message: 'Successful', propertyName: '', invalidValue: '', constraintDescription: '' }];
    }
    return duplicates;
  }

  /** Parsed keys by "region|number", so re-checks after an edit stay cheap on big files. */
  private phoneKeyCache = new Map<string, string>();

  /** E.164 when the number parses, else its digits; '' when there is none. */
  private phoneKey(contact: IContact): string {
    const raw = (contact?.primaryContactNumber ?? '').toString().trim();
    if (!raw) return '';
    const region = (contact as any).countryCode as CountryCode | undefined;
    const cacheKey = `${region ?? ''}|${raw}`;
    const cached = this.phoneKeyCache.get(cacheKey);
    if (cached !== undefined) return cached;
    const parsed = parseFlexiblePhoneNumber(raw, region);
    const key = parsed ? parsed.number : raw.replace(/\D/g, '');
    this.phoneKeyCache.set(cacheKey, key);
    return key;
  }

  /** How many rows fail only because their number has no country code and no country to read it in. */
  get rowsMissingRegion(): number {
    return this.getValidatedContacts.filter((line) => this.lacksRegion(line)).length;
  }

  /**
   * A number with no "+" and no country, that the phone check rejected.
   * Numbers that lost their "+" in Excel (27821234567) already read fine and
   * carry their own country code, so they are left alone.
   */
  private lacksRegion(line: IValidatedContactBatchLine): boolean {
    const contact = line?.personEntity;
    const phone = (contact?.primaryContactNumber ?? '').toString().trim();
    if (!phone || phone.startsWith('+') || (contact as any).countryCode) return false;
    // A repeated number (in the file, or already saved) is not a missing code.
    return (line.constraintViolations ?? []).some(
      (v) =>
        v.propertyName === 'primaryContactNumber' &&
        v.constraintDescription !== DUPLICATE_MARK &&
        !/duplicate/i.test(v.message ?? '')
    );
  }

  /**
   * Reads numbers that have no country code as numbers from `region` (an
   * alpha-2 code such as "BW"), then checks those rows again. Returns how
   * many rows changed. The rows' Country field is left as it was.
   */
  applyDefaultRegion(region: string): number {
    const code = (region || '').toUpperCase();
    if (!code) return 0;
    let changed = 0;
    this.validatedContacts = this.getValidatedContacts;
    for (const line of this.validatedContacts) {
      if (!this.lacksRegion(line)) continue;
      (line.personEntity as any).countryCode = code;
      const violations = this.runLocalValidation(line.personEntity);
      line.batchLineValidationStatus = violations.length ? 'PROCESSING_ERROR' : 'PROCESSED_OK';
      line.constraintViolations = violations.length
        ? violations
        : [{ message: 'Successful', propertyName: '', invalidValue: '', constraintDescription: '' }];
      changed++;
    }
    if (changed) {
      this.flagDuplicateNumbers(this.validatedContacts);
      this.bump();
      this.updateValidatedContactsInStorage();
    }
    return changed;
  }

  /** Drops every row that still needs fixing. Returns how many were removed. */
  removeRowsNeedingFixes(): number {
    this.validatedContacts = this.getValidatedContacts;
    const before = this.validatedContacts.length;
    this.validatedContacts = this.validatedContacts.filter(
      (line) => line.batchLineValidationStatus !== 'PROCESSING_ERROR'
    );
    const removed = before - this.validatedContacts.length;
    if (removed) {
      this.bump();
      this.updateValidatedContactsInStorage();
    }
    return removed;
  }

  /**
   * Rows that could not be imported, as a CSV with the template's column
   * names (so the fixed file maps itself on re-import) plus a Problem column.
   * Numbers starting with "+" come out as '+267… : toCsv's text marker, which
   * also stops Excel turning them into (rounded) numbers. Column mapping
   * strips that marker again, see `stripTextMarker`.
   */
  problemRowsCsv(lines: IValidatedContactBatchLine[]): string {
    const problem = (line: IValidatedContactBatchLine) =>
      (line.constraintViolations ?? [])
        .filter((v) => v.message && v.message !== 'Successful')
        .map((v) => v.message)
        .join('; ');
    const date = (value: unknown) => {
      if (!value) return '';
      const text = String(value);
      return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : text;
    };
    return toCsv(lines, [
      { label: 'firstName', value: (l) => l.personEntity?.firstName },
      { label: 'lastName', value: (l) => l.personEntity?.lastName },
      { label: 'primaryContactNumber', value: (l) => l.personEntity?.primaryContactNumber },
      { label: 'emailAddress', value: (l) => l.personEntity?.emailAddress },
      { label: 'gender', value: (l) => l.personEntity?.gender },
      { label: 'birthDate', value: (l) => date(l.personEntity?.birthDate) },
      { label: 'country', value: (l) => l.personEntity?.country },
      { label: 'city', value: (l) => l.personEntity?.city },
      { label: 'problem', value: (l) => problem(l) || 'Rejected when saving' },
    ]);
  }
}
